import React, { Fragment, useMemo, useEffect, useState } from 'react';
import useApi from 'src/api/api';
import { useLocale } from 'src/provider/LocaleProvider';
import { useFormik } from 'formik';
import utils from 'src/utils/utils';
import LayoutContainer from 'src/layout/LayoutContainer';
import { useSnack } from 'src/provider/SnackbarProvider';
import { useNavigate } from 'react-router';
import { BsFillTriangleFill } from 'react-icons/bs';
import {
  Grid,
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import EditDialog from 'src/components/EditDialog';
import { useBackDrop } from 'src/provider/BackdropProvider';
const _ = require('lodash');
const yup = require('yup');

// Normalize string for comparisons: handle null/undefined, trim whitespace and normalize unicode
const normalizeStr = (s) => {
  const raw = s === undefined || s === null ? '' : s.toString();
  const trimmed = raw.trim();
  const normalized = typeof trimmed.normalize === 'function' ? trimmed.normalize('NFC') : trimmed;
  
  // Handle percentage values: "11.30%" should equal "11.3%"
  // Also handle decimal numbers: "1.50" should equal "1.5"
  const percentMatch = normalized.match(/^(-?\d+\.?\d*)%$/);
  if (percentMatch) {
    // Parse the number, then add % back - this removes trailing zeros
    const num = parseFloat(percentMatch[1]);
    return isNaN(num) ? normalized : `${num}%`;
  }
  
  // Handle plain decimal numbers: "1.50" should equal "1.5"
  const numberMatch = normalized.match(/^-?\d+\.\d+$/);
  if (numberMatch) {
    const num = parseFloat(normalized);
    return isNaN(num) ? normalized : num.toString();
  }
  
  return normalized;
};

const useStyles = makeStyles({
  table: {
    '& .MuiTableRow-root': {
      height: '50px'
    },
    '& .MuiTableCell-root': {
      whiteSpace: 'nowrap',
      textAlign: 'center',
      border: '1px solid rgba(224, 224, 224, 1)'
    },
    '& .borderLeft': {
      borderLeft: '2px solid rgba(224, 224, 224, 0.8)'
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 0.5
    },
    '& .Mui-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: 'limegreen'
    },
    '& .row-existing': {
      backgroundColor: 'rgba(255, 255, 255, 1)', // White background for existing data
      '&:hover': {
        backgroundColor: 'rgba(245, 245, 245, 1)' // Light gray on hover
      }
    },
    '& .row-new': {
      backgroundColor: 'rgba(50, 255, 50,0.2)',
      '&:hover': {
        backgroundColor: 'rgba(50, 255, 50,0.3)'
      }
    },
    '& .row-update': {
      backgroundColor: 'rgba(255, 255, 50,0.25)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 50,0.35)'
      }
    },
    '& .cell-update': {
      backgroundColor: 'rgba(255, 193, 7, 0.7) !important', // Stronger amber background for changed cells - use !important to override row color
      fontWeight: 'bold',
      border: '3px solid rgba(255, 165, 0, 1) !important', // Thicker orange border for maximum visibility
      position: 'relative',
      zIndex: 10 // High z-index to ensure cell updates show above row background
    },
    '& .error': {
      backgroundColor: 'rgba(255, 50, 50,0.2) !important',
      '&:hover': {
        backgroundColor: 'rgba(255, 50, 50,0.3) !important'
      }
    }
  },
  toolbar: {
    display: 'flex',
    '& .left': {
      display: 'flex',
      margin: '-8px',
      width: 'calc(100% - 16px)',
      flexWrap: 'wrap',
      '& .item': {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        whiteSpace: 'nowrap'
      }
    },
    '& .right': {
      display: 'flex',
      margin: '-8px',
      width: 'calc(100% - 16px)',
      flexWrap: 'wrap',
      '& .item': {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        whiteSpace: 'nowrap'
      }
    }
  }
});

const ResourceUploadTable = ({
  existingResources = [],
  rows = [],
  loading = false,
  _orderBy = 'index',
  _order = 'asc',
  rowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25],
  rowsRef,
  formik,
  ...rest
}) => {
  const { t } = useLocale();
  const classes = useStyles();

  const [order, setOrder] = useState(_order);
  const [orderBy, setOrderBy] = useState(_orderBy);

  const sorted = _.orderBy(
    _.map(rows, (r, index) => ({ ...r, index: index + 1 })),
    [orderBy],
    [order]
  );

  const handleSort = (path) => {
    if (orderBy === path) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(path);
      setOrder('asc');
    }
  };

  const headCell = (label, path, sort = true) => {
    if (sort) {
      return (
        <TableSortLabel
          active={orderBy === path}
          direction={orderBy === path ? order : 'asc'}
          onClick={(e) => handleSort(path)}
          hideSortIcon={false}
        >
          {label}
        </TableSortLabel>
      );
    } else return label;
  };

  return (
    <Card {...rest}>
      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '500px' }}>
        <Table size="small" className={classNames(classes.table)}>
          <TableHead>
            <TableRow sx={{ height: '30px' }}>
              <TableCell width="20px">
                {headCell(t('index'), 'index')}
              </TableCell>
              <TableCell>{headCell(t('order'), 'order')}</TableCell>
              <TableCell>{headCell(t('resource_id'), 'id', false)}</TableCell>
              <TableCell className="borderLeft">
                {headCell(t('name_en'), 'name_en', false)}
              </TableCell>
              <TableCell>{headCell(t('name_zh'), 'name_zh')}</TableCell>
              <TableCell>{headCell(t('filters'), 'filters_name')}</TableCell>
              <TableCell>{headCell(t('subjects'), 'subjects_name')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: '40px' }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : _.map(sorted, (row, index) => {
              var errors = _.get(formik.errors, row.index - 1);
              var hasFormikErrors = _.size(errors) > 0;
              var hasStatusError = row.status === 'error';
              var isError = hasFormikErrors || hasStatusError;

              // Use the status field to determine row styling
              const rowClasses = {
                'row-existing': row.status === 'existing' && !isError,
                'row-new': row.status === 'new' && !isError,
                'row-update': false, // Don't color entire row for updates, only individual cells
                error: isError // Highlight both Formik errors and status errors
              };

              // Find existing resource for field comparison
              // ONLY match by ID to avoid false matches on name (names can be duplicates or spaces)
              var existingResource = _.find(
                existingResources,
                (resource) =>
                  row.id && (resource._id === row.id || resource.id === row.id)
              );

              return (
                <>
                  <TableRow
                    style={{ height: 50 }}
                    className={classNames(rowClasses)}
                  >
                    <TableCell>{row.index}</TableCell>
                    <TableCell
                      className={classNames({
                        'cell-update':
                          existingResource &&
                          (existingResource.order || 0) !== (row.order || 0)
                      })}
                    >
                      {row.order || 0}
                    </TableCell>
                    <TableCell>{row.id || '-'}</TableCell>
                    <TableCell
                      className={classNames({
                        'cell-update':
                          existingResource &&
                          normalizeStr(existingResource.name?.en) !==
                            normalizeStr(row.name_en)
                      })}
                    >
                      {row.name_en || '-'}
                    </TableCell>
                    {_.map(
                      ['name_zh', 'filters_name', 'subjects_name'],
                      (path, cellIndex) => {
                        var newValue = _.get(row, path);
                        var oldValue;
                        var displayValue = newValue; // Keep original for display

                        // Get old value based on field type
                            if (existingResource) {
                              if (path === 'name_zh') {
                                // Normalize undefined/null and Unicode, trim both sides
                                oldValue = normalizeStr(existingResource.name?.zh);
                                newValue = normalizeStr(newValue);
                              } else if (path === 'filters_name') {
                                oldValue =
                                  existingResource.filters
                                    ?.map(
                                      (f) =>
                                        f.name?.en || f.name?.zh || f.value || f._id
                                    )
                                    .join(', ') || '';
                                oldValue = normalizeStr(oldValue);
                                newValue = normalizeStr(newValue);
                              } else if (path === 'subjects_name') {
                                oldValue =
                                  existingResource.subjects
                                    ?.map(
                                      (s) =>
                                        s.name?.en || s.name?.zh || s.name || s._id
                                    )
                                    .join(', ') || '';
                                oldValue = normalizeStr(oldValue);
                                newValue = normalizeStr(newValue);
                              }
                            }

                        var isChanged = existingResource && newValue !== oldValue;
                        return (
                          <TableCell
                            key={cellIndex}
                            className={classNames({
                              'cell-update': isChanged,
                              borderLeft: cellIndex === 0
                            })}
                          >
                            {displayValue || '-'}
                          </TableCell>
                        );
                      }
                    )}
                  </TableRow>
                  {(_.size(errors) > 0 || hasStatusError) && (
                    <TableRow>
                      <TableCell colSpan={99} sx={{ px: 1 }}>
                        <Box display="flex" flexDirection="row">
                          <BsFillTriangleFill
                            style={{
                              height: '16px',
                              width: '16px',
                              color: 'red'
                            }}
                          />
                          <Box sx={{ color: 'red', ml: 1, textAlign: 'left' }}>
                            {hasStatusError && (
                              <p style={{ marginTop: 0, marginBottom: 4 }}>
                                {(() => {
                                  if (!row.id || !row.id.trim()) {
                                    return String(
                                      t('error_missing_resource_id')
                                    );
                                  } else if (
                                    !row.name_en?.trim() &&
                                    !row.name_zh?.trim()
                                  ) {
                                    return String(t('error_missing_names'));
                                  } else {
                                    return String(
                                      t('error_resource_id_not_found', {
                                        id: row.id
                                      })
                                    );
                                  }
                                })()}
                              </p>
                            )}
                            {_.size(errors) > 0 && _.keys(errors).map(fieldName => {
                              return <p key={fieldName} style={{ marginTop: 0, marginBottom: 4 }}>{`${t(fieldName)} : ${errors[fieldName]}`}</p>;
                            })}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
            {!loading && _.size(rows) === 0 && (
              <TableRow style={{ height: 50 }}>
                <TableCell colSpan={99}>
                  <Typography variant="h5">{t('no_records')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};

const LearningResourceUpload = () => {
  const api = useApi();
  const nav = useNavigate();

  const { t } = useLocale();

  const backDrop = useBackDrop();
  const snack = useSnack();
  const [existingResources, setExistingResources] = useState([]);
  const [newResourceList, setNewResourceList] = useState([]);
  const [open, setOpen] = useState();
  const [availableFilters, setAvailableFilters] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [isLoadingSourceData, setIsLoadingSourceData] = useState(true);
  const [sourceDataError, setSourceDataError] = useState(null);
  const [pendingCsvFile, setPendingCsvFile] = useState(null);

  useEffect(() => {
    // Load existing resources, filters, and subjects
    setIsLoadingSourceData(true);
    setSourceDataError(null);
    Promise.all([
      api.getResources({
        page: 1,
        limit: 1000,
        filters: [],
        subjects: [],
        name: ''
      }),
      api.getOrganizationFilters(),
      api.getSubjects()
    ]).then(([resourcesRes, filtersRes, subjectsRes]) => {
      const existingData = resourcesRes.data.resources || [];
      setExistingResources(existingData);
      setAvailableFilters(filtersRes.data || []);
      setAvailableSubjects(subjectsRes.data.subjects || []);
      setIsLoadingSourceData(false);
    }).catch((error) => {
      console.error('Error loading source data:', error);
      setSourceDataError(error.message || 'Failed to load source data');
      setIsLoadingSourceData(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process pending CSV file when source data becomes ready
  useEffect(() => {
    if (!isLoadingSourceData && pendingCsvFile && !sourceDataError) {
      console.log('Source data ready, processing pending CSV file...');
      const processCsv = async () => {
        const file = pendingCsvFile;
        setPendingCsvFile(null);
        try {
          await handleCsv(file);
        } catch (error) {
          console.error('Error processing pending CSV:', error);
          setIsLoadingData(false);
        }
      };
      
      processCsv();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingSourceData, pendingCsvFile, sourceDataError]);

  // Helper function to validate filters/subjects names against available options
  const validateFilterSubjectNames = (namesString, availableItems, fieldType) => {
    if (!namesString || !namesString.trim()) {
      return { valid: true, invalidNames: [] };
    }
    
    const names = namesString.split(',').map((name) => name.trim()).filter(Boolean);
    const invalidNames = names.filter((name) => {
      const found = availableItems.find(
        (item) =>
          item.name?.en === name ||
          item.name?.zh === name ||
          item.name === name
      );
      return !found;
    });
    
    return {
      valid: invalidNames.length === 0,
      invalidNames: invalidNames
    };
  };

  const schema = yup.array().of(
    yup
      .object()
      .shape({
        id: yup.string().required(t('yup_required')), // ID is now required for all resources
        name_en: yup.string().nullable(),
        name_zh: yup.string().nullable(),
        filters_name: yup.string().nullable(), // Filters are optional
        subjects_name: yup.string().nullable(), // Subjects are optional
        order: yup
          .number()
          .typeError(t('yup_number_type_error'))
          .required(t('yup_number_required'))
          .min(0, t('yup_number_min', { min: 0 }))
          .integer(t('yup_integer')),
        status: yup.string().oneOf(['existing', 'update', 'error']).nullable() // Remove 'new' status
      })
      .test('name-required', t('name_required'), function (value) {
        return value.name_en || value.name_zh;
      })
      .test('filters-valid', t('invalid_filters'), function (value) {
        const validation = validateFilterSubjectNames(value.filters_name, availableFilters, 'filter');
        if (!validation.valid) {
          return this.createError({
            path: this.path + '.filters_name',
            message: t('invalid_filters') + ': ' + validation.invalidNames.join(', ')
          });
        }
        return true;
      })
      .test('subjects-valid', t('invalid_subjects'), function (value) {
        const validation = validateFilterSubjectNames(value.subjects_name, availableSubjects, 'subject');
        if (!validation.valid) {
          return this.createError({
            path: this.path + '.subjects_name',
            message: t('invalid_subjects') + ': ' + validation.invalidNames.join(', ')
          });
        }
        return true;
      })
  );

  const formik = useFormik({
    initialValues: newResourceList || [],
    enableReinitialize: true,
    validationSchema: schema,
    validate: () => {}, // Enable validation on change
    validateOnChange: true, // Validate on every change
    validateOnBlur: true, // Validate on blur
    onSubmit: async (values, helper) => {
      backDrop.open();
      try {
        let successCount = 0;
        let errorCount = 0;

        // Only process updated resources that are valid (exclude error status)
        const toProcess = values.filter(
          (resource) =>
            resource.status === 'update' && resource.status !== 'error'
        );

        for (const resource of toProcess) {
          try {
            // Validate that resource has ID (should always be true due to schema validation)
            if (!resource.id) {
              console.error(
                `Resource "${
                  resource.name_en || resource.name_zh
                }" has no ID - skipping`
              );
              errorCount++;
              continue;
            }

            // Map filter names to ObjectIds
            let filters = [];
            if (resource.filters_name && resource.filters_name.trim()) {
              const filterNames = resource.filters_name
                .split(',')
                .map((name) => name.trim());
              filters = filterNames
                .map((name) => {
                  const filter = availableFilters.find(
                    (f) =>
                      f.name?.en === name ||
                      f.name?.zh === name ||
                      f.name === name
                  );
                  if (!filter && name) {
                    console.warn(
                      `Filter "${name}" not found in available filters`
                    );
                  }
                  return filter?._id;
                })
                .filter(Boolean); // Remove undefined values
            }

            // Map subject names to ObjectIds
            let subjects = [];
            if (resource.subjects_name && resource.subjects_name.trim()) {
              const subjectNames = resource.subjects_name
                .split(',')
                .map((name) => name.trim());
              subjects = subjectNames
                .map((name) => {
                  const subject = availableSubjects.find(
                    (s) =>
                      s.name?.en === name ||
                      s.name?.zh === name ||
                      s.name === name
                  );
                  if (!subject && name) {
                    console.warn(
                      `Subject "${name}" not found in available subjects`
                    );
                  }
                  return subject?._id;
                })
                .filter(Boolean); // Remove undefined values
            }

            console.log(
              `Processing resource "${resource.name_en || resource.name_zh}":`,
              {
                status: resource.status,
                id: resource.id,
                filters: filters,
                subjects: subjects,
                filtersCount: filters.length,
                subjectsCount: subjects.length
              }
            );

            // Update existing resource (only option now)
            const resourceData = {
              id: resource.id, // Include the ID in the resource data
              name_en: resource.name_en || '',
              name_zh: resource.name_zh || '',
              order: resource.order,
              filters: filters,
              subjects: subjects
            };

            await api.putResources(resourceData, resource.id);
            successCount++;
          } catch (err) {
            console.error(
              `Error processing resource "${
                resource.name_en || resource.name_zh
              }":`,
              err
            );
            errorCount++;
          }
        }

        if (errorCount === 0) {
          nav('/admin/learningresource');
          snack.open(t('upload_success'));
        } else {
          snack.open(
            t('import_completed', { success: successCount, errors: errorCount })
          );
        }
      } catch (e) {
        console.error('Upload error:', e);
        snack.error(t('upload_fail'));
      } finally {
        backDrop.close();
        helper.setSubmitting(false);
      }
    }
  });

  // Trigger validation whenever newResourceList changes (like after CSV import)
  useEffect(() => {
    if (newResourceList.length > 0) {
      // Small delay to ensure formik has been updated with new values
      const timer = setTimeout(() => {
        formik.validateForm().then(() => {
          console.log('Validation completed after data import');
        });
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newResourceList]);

  const handleExport = () => {
    utils.exportCsv(
      [],
      [
        { label: 'Order', value: (row) => row.order },
        { label: 'Resource_ID', value: (row) => row.id },
        { label: 'Name_EN', value: (row) => row.name_en },
        { label: 'Name_ZH', value: (row) => row.name_zh },
        { label: 'Filters_Name', value: (row) => row.filters_name },
        { label: 'Subjects_Name', value: (row) => row.subjects_name },        
      ],
      'learning_resource_template'
    );
  };

  const handleExportCurrentResources = () => {
    // Export all existing resources with current data, sorted by order
    const sortedResources = _.orderBy(existingResources, ['order'], ['asc']);
    const currentResourcesData = sortedResources.map((resource) => ({
      id: resource._id || resource.id,
      name_en: resource.name?.en || '',
      name_zh: resource.name?.zh || '',
      filters_name:
        resource.filters
          ?.map((f) => f.name?.en || f.name?.zh || f.value || f._id)
          .join(', ') || '',
      subjects_name:
        resource.subjects
          ?.map((s) => s.name?.en || s.name?.zh || s.name || s._id)
          .join(', ') || '',
      order: resource.order || 0
    }));

    utils.exportCsv(
      currentResourcesData,
      [
        { label: 'Order', value: (row) => row.order },
        { label: 'Resource_ID', value: (row) => row.id },
        { label: 'Name_EN', value: (row) => row.name_en },
        { label: 'Name_ZH', value: (row) => row.name_zh },
        { label: 'Filters_Name', value: (row) => row.filters_name },
        { label: 'Subjects_Name', value: (row) => row.subjects_name }
      ],
      'learning_resources_data'
    );
  };

  const handleCsv = async (f) => {
    setIsLoadingData(true);
    
    // If source data is still loading, store the file and wait
    if (isLoadingSourceData) {
      setPendingCsvFile(f);
      setHasUploadedFile(true);
      return;
    }
    
    try {
      var csv = await utils.readFileAsText(f);
      var arr = await utils.csv2JsonArray(csv, {
      Resource_ID: 'id',
      ID: 'id', 
      Name_EN: 'name_en',
      Name_ZH: 'name_zh',
      Filters_Name: 'filters_name',
      Subjects_Name: 'subjects_name',
      Order: 'order'
    });

    const validCsvData = _.filter(
      arr,
      (item) =>
        item.id?.trim() ||
        item.name_en?.trim() ||
        item.name_zh?.trim() ||
        item.filters_name?.trim() ||
        item.subjects_name?.trim() ||
        item.order
    );

    // Compare with existing data and assign status
    const processedData = validCsvData.map((csvRow) => {
      // Convert order to number but keep the original value for validation
      let orderValue = csvRow.order;
      if (
        orderValue !== undefined &&
        orderValue !== null &&
        orderValue !== ''
      ) {
        const parsedOrder = parseInt(orderValue);
        if (!isNaN(parsedOrder)) {
          orderValue = parsedOrder;
        } else {
          // Keep invalid string values so validation can catch them
          orderValue = csvRow.order;
        }
      } else {
        orderValue = 0; // Default to 0 if empty
      }

      // Check for required fields: ID and at least one name
      if (!csvRow.id || !csvRow.id.trim()) {
        return {
          ...csvRow,
          order: orderValue,
          status: 'error' // Mark as error if no ID provided
        };
      }

      // Check if at least one name is provided
      if (!csvRow.name_en?.trim() && !csvRow.name_zh?.trim()) {
        return {
          ...csvRow,
          order: orderValue,
          status: 'error' // Mark as error if no names provided
        };
      }

      // Check if this resource ID exists in the system
      const existing = _.find(
        existingResources,
        (resource) => resource._id === csvRow.id || resource.id === csvRow.id
      );

      if (!existing) {
        // Resource ID not found - mark as invalid
        return {
          ...csvRow,
          order: orderValue,
          status: 'error' // Mark as error if resource ID doesn't exist
        };
      }

      // Check if any fields have changed (update)
      const existingFiltersName =
        existing.filters
          ?.map((f) => f.name?.en || f.name?.zh || f.value || f._id)
          .join(', ') || '';

      const existingSubjectsName =
        existing.subjects
          ?.map((s) => s.name?.en || s.name?.zh || s.name || s._id)
          .join(', ') || '';

      const hasChanges =
        normalizeStr(existing.name?.en) !== normalizeStr(csvRow.name_en) ||
        normalizeStr(existing.name?.zh) !== normalizeStr(csvRow.name_zh) ||
        normalizeStr(existingFiltersName) !== normalizeStr(csvRow.filters_name) ||
        normalizeStr(existingSubjectsName) !== normalizeStr(csvRow.subjects_name) ||
        (existing.order || 0) !==
          (typeof orderValue === 'number'
            ? orderValue
            : parseInt(orderValue) || 0);

      const status = hasChanges ? 'update' : 'existing';

      return {
        ...csvRow,
        id: csvRow.id, // Keep the original ID from CSV
        order: orderValue, // Keep the processed order value
        status
      };
    });

    // Combine existing data with new imported data
    // Keep existing resources that weren't in the CSV (including those that would match invalid CSV entries)
    const existingNotInCsv = existingResources
      .filter((existingResource) => {
        // Only exclude existing resources that have valid CSV matches (not error status matches)
        return !processedData.find(
          (csvRow) =>
            csvRow.status !== 'error' &&
            csvRow.id &&
            (existingResource._id === csvRow.id ||
              existingResource.id === csvRow.id)
        );
      })
      .map((resource) => ({
        id: resource._id || resource.id,
        name_en: resource.name?.en || '',
        name_zh: resource.name?.zh || '',
        filters_name:
          resource.filters
            ?.map((f) => f.name?.en || f.name?.zh || f.value || f._id)
            .join(', ') || '',
        subjects_name:
          resource.subjects
            ?.map((s) => s.name?.en || s.name?.zh || s.name || s._id)
            .join(', ') || '',
        order: resource.order || 0,
        status: 'existing'
      }));

    // Combine all data: existing not in CSV + all processed CSV data (including errors)
    const combinedData = [...existingNotInCsv, ...processedData];

    // Filter to show only updated or error records
    const filteredData = combinedData.filter(
      item => item.status === 'update' || item.status === 'error'
    );

    if (processedData.length > 0 && filteredData.length === 0) {
      snack.error(t('no_changes_detected') || 'No changes detected in the uploaded file.');
    }

    setNewResourceList(filteredData);
    setHasUploadedFile(true);
    } catch (error) {
      console.error('Error processing CSV:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Validation will be triggered automatically by formik when values change
  // No need for manual useEffect here

  const handleConfirm = async () => {
    // Allow confirmation even with errors, but we'll only process valid updates
    const hasValidUpdates = newResourceList.some((r) => r.status === 'update');
    if (hasValidUpdates) {
      setOpen(true);
    }
  };

  var count = useMemo(() => {
    const statusCount = _.countBy(
      newResourceList,
      (r) => r.status || 'existing'
    );

    // Count both status errors and formik validation errors
    const statusErrors = newResourceList.filter(
      (r) => r.status === 'error'
    ).length;
    const formikErrors = _.size(formik.errors);
    const totalErrors = statusErrors + formikErrors;

    if (totalErrors > 0) {
      statusCount.error = totalErrors;
    }

    return statusCount;
  }, [newResourceList, formik.errors]);

  // Check if there are any valid changes (update items that are not errors)
  const hasValidChanges = useMemo(() => {
    const validUpdates = newResourceList.filter(
      (r) => r.status === 'update'
    ).length;
    // Disable save button if there are any errors (status errors or formik errors)
    const statusErrors = newResourceList.filter((r) => r.status === 'error').length;
    const formikErrors = _.size(formik.errors);
    const hasErrors = statusErrors > 0 || formikErrors > 0;
    
    return validUpdates > 0 && !hasErrors;
  }, [newResourceList, formik.errors]);

  return (
    <LayoutContainer
      title={t('update_learning_resources')}
      backSrc="/admin/learningresource"
    >
      <Grid container spacing={1} mb={2}>
        <Grid
          item
          xs
          className="flex ac"
          sx={{ minWidth: 'min(100%,300px)', width: '200px' }}
        >
          <Grid container spacing={1}>
            <Grid item className="flex ac">
              <Box
                height="20px"
                width="20px"
                sx={{
                  backgroundColor: 'rgba(255, 255, 50,0.2)!important',
                  mr: 1,
                  border: '2px black solid'
                }}
              />
              <span>
                {' '}
                {t('update_resource')} ({count.update || 0})
              </span>
            </Grid>
            <Grid item className="flex ac">
              <Box
                height="20px"
                width="20px"
                sx={{
                  backgroundColor: 'rgba(255, 50, 50,0.2) !important',
                  mr: 1,
                  border: '2px black solid'
                }}
              />
              <span>
                {' '}
                {t('invalid_data')} ({count.error || 0})
              </span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={1}>
            <Grid item className="flex ac">
              <Button onClick={handleExport} variant="outlined">
                {t('download_update_template')}
              </Button>
            </Grid>
            <Grid item className="flex ac">
              <Button onClick={handleExportCurrentResources} variant="outlined">
                {t('download_current_resources')}
              </Button>
            </Grid>
            <Grid item className="flex ac">
              <Button
                onClick={() => utils.readFile(handleCsv, '.csv')}
              >
                {t('upload_update_file')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {sourceDataError && (
        <Box
          mb={2}
          p={2}
          sx={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: 1 }}
        >
          <Typography variant="body1">
            <strong>{t('error')}:</strong> {sourceDataError}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('please_refresh_and_try_again') ||
              'Please refresh the page and try again.'}
          </Typography>
        </Box>
      )}

      <Box mb={2}>
        <Typography variant="h6" gutterBottom sx={{ color: '#d32f2f' }}>
          {t('update_existing_resources_only')} - {t('no_new_resource_creation')}
        </Typography>

        <Typography variant="h6" gutterBottom>
          {t('learning_resource_update_guide_title')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('learning_resource_update_step1_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('learning_resource_update_step1_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('learning_resource_update_step2_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('learning_resource_update_step2_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('learning_resource_update_step3_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('learning_resource_update_step3_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('learning_resource_update_step4_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('learning_resource_update_step4_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('learning_resource_update_step5_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('learning_resource_update_step5_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mt: 1, mb: 0.5 }}>
          {t('learning_resource_update_note_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ color: '#666' }}>
          • {t('learning_resource_update_note1')}
          <br />• {t('learning_resource_update_note2')}
        </Typography>
      </Box>
      <ResourceUploadTable
        formik={formik}
        rows={newResourceList}
        existingResources={existingResources}
        _orderBy="order"
        _order="asc"
        loading={isLoadingData || (pendingCsvFile !== null)}
      />
      {hasUploadedFile && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button onClick={handleConfirm} disabled={!hasValidChanges}>
            {t('save')}
          </Button>
        </Box>
      )}
      <EditDialog
        title={t('confirm_update_learning_resources')}
        maxWidth="xs"
        confirmDisabled={formik.isSubmitting}
        handleSave={formik.handleSubmit}
        open={open}
        handleClose={() => setOpen(false)}
        confirmText={t('confirm')}
      >
        {count?.update > 0 && (
          <p>{t('update_a_resources', { a: count?.update })}</p>
        )}
        {count?.error && count?.error > 0 && (
          <p style={{ color: '#d32f2f' }}>
            {t('invalid_rows_will_be_skipped', { count: count.error })}
          </p>
        )}
      </EditDialog>
    </LayoutContainer>
  );
};

export default LearningResourceUpload;
